import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

type NotificationCardProps = {
  title: string;
  description: ReactNode;
  icon: ReactNode;
  variant?: 'alert-contact' | 'alert-action' | 'alert-default';
  time?: ReactNode;
  action?: ReactNode;
  onClick?: () => void;
  onDismiss: () => void | Promise<void>;
  dismissLabel?: string;
  role?: 'status';
  busy?: boolean;
  autoDismissMs?: number;
};

export function NotificationCard({
  title, description, icon, variant = 'alert-default', time, action,
  onClick, onDismiss, dismissLabel = 'Dismiss alert', role, busy = false, autoDismissMs = 20000,
}: NotificationCardProps) {
  const [visible, setVisible] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const dismissRef = useRef(onDismiss);
  useEffect(() => { dismissRef.current = onDismiss; }, [onDismiss]);
  const dismiss = useCallback(async () => {
    setDismissing(true);
    try {
      await dismissRef.current();
    } catch (error) {
      console.error('Error dismissing alert:', error);
      setDismissing(false);
    }
  }, []);
  useEffect(() => {
    const entrance = setTimeout(() => setVisible(true), 100);
    const timeout = setTimeout(() => void dismiss(), autoDismissMs);
    return () => { clearTimeout(entrance); clearTimeout(timeout); };
  }, [dismiss, autoDismissMs]);
  return (
    <div
      className={`alert-notification ${variant} ${visible ? 'visible' : ''} ${dismissing || busy ? 'dismissing' : ''} ${onClick ? 'clickable' : ''}`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      role={role}
    >
      <div className="alert-content">
        <div className="alert-icon">{icon}</div>
        <div className="alert-body">
          <div className="alert-title">{title}</div>
          <div className="alert-description">{description}</div>
          {action}
          {time && <div className="alert-time">{time}</div>}
        </div>
        <button className="alert-dismiss" type="button" aria-label={dismissLabel}
          disabled={dismissing || busy} onClick={(event) => { event.stopPropagation(); void dismiss(); }}>
          <i className="fa-solid fa-times" />
        </button>
      </div>
    </div>
  );
}
